import React, { useState, useEffect } from 'react';
import { Button } from 'react-daisyui';
import { FaPaperPlane, FaCopy, FaVolumeUp, FaPause } from 'react-icons/fa'; // Import the send, copy, volume, and pause icons
import { marked } from 'marked'; // Import the marked library
import './App.css'; // Import the CSS file
import content from './content.json'; // Import the content JSON file

const inappropriateWords = ['badword1', 'badword2', 'badword3']; // Add inappropriate words here
const inappropriateTopics = ['inappropriate topic1', 'inappropriate topic2', 'inappropriate topic3']; // Add inappropriate topics here

const App = () => {
    const [query, setQuery] = useState('');
    const [response, setResponse] = useState('');
    const [displayedResponse, setDisplayedResponse] = useState('');
    const [language, setLanguage] = useState('en'); // Default language is English
    const [isLoading, setIsLoading] = useState(false); // Loading state
    const [submittedQuery, setSubmittedQuery] = useState(''); // Store the submitted query
    const [isResponseComplete, setIsResponseComplete] = useState(false); // Track if the response animation is complete
    const [isSpeaking, setIsSpeaking] = useState(false); // Track if text-to-speech is active
    const [apiError, setApiError] = useState(false); // Track if there was an API error
    const [showSpotifyPlayer, setShowSpotifyPlayer] = useState(false); // Track if the Spotify player should be shown
    const [showNewsletterButton, setShowNewsletterButton] = useState(false); // Track if the newsletter button should be shown
    const [showPredefinedQuestions, setShowPredefinedQuestions] = useState(true); // Track if the predefined questions should be shown

    useEffect(() => {
        if (apiError) return; // Do not proceed if there was an API error
        let index = 0;
        setDisplayedResponse(''); // Reset the displayed response for the animation
        setIsResponseComplete(false); // Reset the response complete state
        const interval = setInterval(() => {
            if (index <= response.length) {
                setDisplayedResponse(response.slice(0, index));
                index++;
            } else {
                clearInterval(interval);
                setIsResponseComplete(true); // Set the response complete state to true
                if (response.includes('AI + Automation Chronicles')) {
                    setShowSpotifyPlayer(true); // Show the Spotify player if the response contains the specific theme
                    setShowNewsletterButton(true); // Show the newsletter button if the response contains the specific theme
                } else {
                    setShowSpotifyPlayer(false); // Hide the Spotify player otherwise
                    setShowNewsletterButton(false); // Hide the newsletter button otherwise
                }
            }
        }, 10); // Adjust the speed of the animation here
        return () => clearInterval(interval);
    }, [response, apiError]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (inappropriateWords.some(word => query.toLowerCase().includes(word)) || inappropriateTopics.some(topic => query.toLowerCase().includes(topic))) {
            alert('Your question contains inappropriate language or topics. Please rephrase your question.');
            return;
        }
        setIsLoading(true); // Set loading state to true
        setSubmittedQuery(query); // Store the submitted query
        setShowPredefinedQuestions(false); // Hide predefined questions
        const requestBody = {
            projectId: content[language].projectId,
            query: query,
            language: language // Add the language property
        };

        try {
            const res = await fetch(content[language].apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            const data = await res.json();
            if (!data.response) {
                throw new Error('Invalid response from API');
            }
            setResponse(data.response); // Extract only the response text
            setQuery(''); // Clear the input text
            setApiError(false); // Reset API error state
        } catch (error) {
            console.error('Error:', error);
            setApiError(true); // Set API error state
        } finally {
            setIsLoading(false); // Set loading state to false
        }
    };

    const handleLanguageChange = (e) => {
        setLanguage(e.target.value);
    };

    const handleCopy = () => {
        const textArea = document.createElement('textarea');
        textArea.value = response;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert(getContent('copyAlert'));
    };

    const handleTextToSpeech = () => {
        if (isSpeaking) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        } else {
            const speech = new SpeechSynthesisUtterance(response);
            speech.lang = language === 'en' ? 'en-US' : language === 'de' ? 'de-DE' : 'fr-FR';
            window.speechSynthesis.speak(speech);
            setIsSpeaking(true);
        }
    };

    const handlePredefinedQuestion = async (question) => {
        setQuery(question);
        setIsLoading(true); // Set loading state to true
        setSubmittedQuery(question); // Store the submitted query
        setShowPredefinedQuestions(false); // Hide predefined questions
        const requestBody = {
            projectId: content[language].projectId,
            query: question,
            language: language // Add the language property
        };

        try {
            const res = await fetch(content[language].apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            const data = await res.json();
            if (!data.response) {
                throw new Error('Invalid response from API');
            }
            setResponse(data.response); // Extract only the response text
            setQuery(''); // Clear the input text
            setApiError(false); // Reset API error state
        } catch (error) {
            console.error('Error:', error);
            setApiError(true); // Set API error state
        } finally {
            setIsLoading(false); // Set loading state to false
        }
    };

    const getContent = (key) => {
        return content[language][key];
    };

    return (
        <div>
            <div className="header-image">
                <div className="header-frame">
                    <img src="/demo2.gif" alt="Header" />
                </div>
                <div className="header-text">
                    <h1>{getContent('headerTitle')}</h1>
                    <p>{getContent('headerSubtitle')}</p>
                </div>
            </div>
            <form onSubmit={handleSubmit} className="form">
                <div className="input-container">
                    <select id="language-select" onChange={handleLanguageChange} value={language} className="language-select">
                        <option value="en">EN</option>
                        <option value="de">DE</option>
                        <option value="fr">FR</option>
                    </select>
                    <input
                        id="query-input"
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={getContent('inputPlaceholder')}
                        className="input"
                    />
                    <Button type="submit" className="button">
                        <FaPaperPlane />
                    </Button>
                </div>
            </form>
            {showPredefinedQuestions && (
                <div className="predefined-questions">
                    <Button onClick={() => handlePredefinedQuestion(getContent('predefinedQuestion1'))} className="predefined-button">
                        {getContent('predefinedButton1')}
                    </Button>
                    <Button onClick={() => handlePredefinedQuestion(getContent('predefinedQuestion2'))} className="predefined-button">
                        {getContent('predefinedButton2')}
                    </Button>
                    <Button onClick={() => handlePredefinedQuestion(getContent('predefinedQuestion3'))} className="predefined-button">
                        {getContent('predefinedButton3')}
                    </Button>
                    <Button onClick={() => window.open(getContent('contactLink'), '_blank')} className="predefined-button">
                        {getContent('predefinedButton4')}
                    </Button>
                </div>
            )}
            {isLoading ? (
                <div className="invisible-box">
                    <div className="question-rectangle">
                        {submittedQuery}
                    </div>
                    <div className="thinking-animation">
                        <h4>{getContent('thinkingText')}</h4>
                    </div>
                </div>
            ) : response ? (
                <>
                    <div className="invisible-box">
                        <div className="question-rectangle">
                            {submittedQuery}
                        </div>
                        <div className="response-rectangle">
                            <div dangerouslySetInnerHTML={{ __html: marked(displayedResponse) }} />
                            {isResponseComplete && (
                                <>
                                    <Button onClick={handleCopy} className="copy-button">
                                        <FaCopy />
                                    </Button>
                                    <Button onClick={handleTextToSpeech} className="tts-button">
                                        {isSpeaking ? <FaPause /> : <FaVolumeUp />}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                    {isResponseComplete && (
                        <div className="invisible-box">
                            {showSpotifyPlayer && (
                                <div style={{ marginTop: '20px' }}>
                                    <iframe
                                        src="https://open.spotify.com/embed/show/41pj6fe8a74BFZJn2uhchH?si=b60304aec2b74749"
                                        width="100%"
                                        height="152"
                                        frameBorder="0"
                                        allowtransparency="true"
                                        allow="encrypted-media"
                                        title="spotify-player"
                                    ></iframe>
                                </div>
                            )}
                            {showNewsletterButton && (
                               <a className="libutton" href="https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7264202984408936448" target="_blank" rel="noreferrer">
                                    Subscribe on LinkedIn
                                </a>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className="default-message">
                    {getContent('defaultMessage')}
                </div>
            )}
            <div className="additional-info">
                <h2>{getContent('hintsTitle')}</h2>
                <p dangerouslySetInnerHTML={{ __html: getContent('hintsText') }}></p>
            </div>
            <footer className="footer">
                {getContent('footerText')}
            </footer>
        </div>
    );
};

export default App;